import Pill from 'ext-ui/components/Pill/Pill'
import Tooltip from 'ext-ui/components/Tooltip/Tooltip'
import React, { ReactElement } from 'react'
import SvgTooltip from 'src/components/Svgs/SvgTooltip'
import { ExtendCurrency } from 'src/enums'
import { useCurrencyFormatter } from 'src/localization/Crncy'
import { useTranslate } from 'src/localization/Trns'
import { toDollars } from 'src/utils/currency.utils'

import type { CompanyLineSummary } from './SpendLimitTile'

type LimitSummaryProps = {
  /** Pre-formatted "Card limit" figure, or the "Loading" / "Unavailable" / "Not available" copy. */
  limitValueText: string
  showAdjustablePill: boolean
  companyLine?: CompanyLineSummary
  /** Already resolved to `ExtendCurrency` by the caller — see `LimitAdjustSection`'s prop of the same name. */
  companyLineCurrency: ExtendCurrency
  canApplyDirectly: boolean
  /**
   * Pre-formatted "Resets <date>" copy. There is no reset-date field anywhere
   * in this codebase yet (same gap as the hero's credit row — see the design
   * VQA §4.4 caveat), so no caller currently passes this and the row does not
   * render. Structurally ready for when the field exists.
   */
  resetsAtLabel?: string
}

/** Read-only "Card limit" / "Company line" / "Resets" rows. No editing UI. */
export default function LimitSummary(props: LimitSummaryProps): ReactElement {
  const {
    limitValueText,
    showAdjustablePill,
    companyLine,
    companyLineCurrency,
    canApplyDirectly,
    resetsAtLabel,
  } = props
  const translate = useTranslate()
  const formatCurrency = useCurrencyFormatter()

  return (
    <>
      <div className="SpendLimitTile-kv">
        <span className="SpendLimitTile-kvLabel">
          {translate('Card limit')}
          {showAdjustablePill && (
            <Pill size="small" color="highlight">
              {translate('Adjustable')}
            </Pill>
          )}
        </span>
        <span
          className="SpendLimitTile-kvValue strong"
          data-testid="spend-limit-current"
        >
          {limitValueText}
        </span>
      </div>

      {companyLine !== undefined && canApplyDirectly && (
        <div className="SpendLimitTile-kv" data-testid="company-line">
          <span className="SpendLimitTile-kvLabel">
            {translate('Company line')}
            {/*
              A real <button>, not ext-ui's TooltipIcon: that primitive
              renders its trigger as a plain <div>, so MUI never receives
              focus and the content is hover-only (WCAG 2.1.1 / 1.4.13).
              The aria-label carries the same sentence, so assistive tech
              gets it without opening the tooltip at all.
            */}
            <Tooltip
              tooltipContent={translate(
                'The commercial line this card draws from. Read-only.',
              )}
            >
              <button
                type="button"
                className="SpendLimitTile-kvInfo"
                aria-label={translate(
                  'The commercial line this card draws from. Read-only.',
                )}
                data-testid="company-line-info"
              >
                <SvgTooltip width={14} height={14} viewBox="0 0 24 24" />
              </button>
            </Tooltip>
          </span>
          <span className="SpendLimitTile-kvValue">
            {translate('${available} available of ${total}', {
              available: formatCurrency({
                amount: toDollars(companyLine.availableCredit),
                currency: companyLineCurrency,
                maximumFractionDigits: 0,
              }),
              total: formatCurrency({
                amount: toDollars(companyLine.totalCredit),
                currency: companyLineCurrency,
                maximumFractionDigits: 0,
              }),
            })}
          </span>
        </div>
      )}

      {resetsAtLabel !== undefined && (
        <div className="SpendLimitTile-kv" data-testid="spend-limit-resets">
          <span className="SpendLimitTile-kvLabel">{translate('Resets')}</span>
          <span className="SpendLimitTile-kvValue">{resetsAtLabel}</span>
        </div>
      )}
    </>
  )
}
